import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { CheckIcon } from "@heroicons/react/24/outline";
import { useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const InvoiceFormPO = () => {
  const { poNumber } = useParams();
  const [poData, setPoData] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [amount, setAmount] = useState(0);
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split("T")[0]);
  const [previousInvoices, setPreviousInvoices] = useState([]);
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
 useEffect(() => {
  const fetchPOData = async () => {
    try {
      const token = localStorage.getItem("token");
      const encodedPoNumber = encodeURIComponent(poNumber);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/purchasing-order/${encodedPoNumber}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const result = await response.json();

      // ✅ tampilkan semua data detail di console
      console.group("📦 Purchase Order Detail");
      console.log("Full PO Data:", result);
      console.log("ID:", result?.data?.id);
      console.log("Nomor PO:", result?.data?.number);
      console.log("Status:", result?.data?.status);
      console.log("Distributor:", result?.data?.distributor);
      console.log("Outlet:", result?.data?.outlet);
      console.log("Items:", result?.data?.items);
      console.log("Sub Total:", result?.data?.sub_amount);
      console.log("Total Amount:", result?.data?.total_amount);
      console.log("Deskripsi:", result?.data?.description);
      console.groupEnd();

      setPoData(result.data);
    } catch (error) {
      console.error("❌ Error fetching PO data:", error);
    }
  };

  fetchPOData();
}, [poNumber]);

  useEffect(() => {
    const fetchPreviousInvoices = async () => {
      if (!poData?.id) return;
  
      try {
        const token = localStorage.getItem("token");
  
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/v1/invoice?po_id=${poData.id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
  
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const result = await response.json();
        setPreviousInvoices(result.data || []);
      } catch (error) {
        console.error("Error fetching previous invoices:", error);
      }
    };
  
    fetchPreviousInvoices();
  }, [poData]);
  

  const formatRupiah = (value) => {
  if (value === null || value === undefined) return "Rp 0";
  // menampilkan angka asli tanpa pembulatan dua desimal
  const formatted = value.toLocaleString("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 10, // tampilkan desimal penuh
  });
  return `Rp ${formatted}`;
};


 const handleSave = async () => {
  try {
    const token = localStorage.getItem("token");
    const payload = {
      id_po: poData?.id || 0,
      number: invoiceNumber,
      amount: Number(amount),
      description: description || `Pembayaran ${poData?.number} #${previousInvoices.length + 1}`,
      due_date: dueDate,
      paid_date: paidDate,
    };

    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/invoice`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to submit invoice. Status: ${response.status}`);
    }

    const result = await response.json();
    console.log("Invoice response:", result);

    // ✅ SweetAlert success
    MySwal.fire({
      icon: "success",
      title: "Berhasil!",
      text: "Invoice berhasil disimpan",
      showConfirmButton: false,
      timer: 2000,
    }).then(() => {
      // redirect ke /inventory/invoice setelah popup hilang
      navigate("/inventory/invoice");
    });

  } catch (error) {
    console.error("Error saving invoice:", error);

    // ❌ SweetAlert error
    MySwal.fire({
      icon: "error",
      title: "Gagal",
      text: "Terjadi kesalahan saat menyimpan invoice. Silakan coba lagi.",
    });
  }
};

const SuccessModal = ({ message, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl p-6 shadow-lg max-w-sm w-full text-center">
        <h2 className="text-green-600 text-lg font-semibold mb-4">Berhasil!</h2>
        <p className="text-gray-700">{message}</p>
        <button
          className="mt-6 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
          onClick={onClose}
        >
          OK
        </button>
      </div>
    </div>
  );
};

  if (!poData) return <p>Loading...</p>;

  const { number, status, distributor, outlet, items, total_amount, created_at } = poData;
  const previousTotal = previousInvoices.reduce((acc, inv) => acc + inv.amount, 0);
  const estimatedAmount = total_amount - previousTotal;
  const remainingAmount = estimatedAmount - Number(amount);
  return (
    <div className="max-w-7xl mx-auto p-8 bg-white shadow-lg rounded-lg">
      <div className="grid grid-cols-2 gap-8">
        {/* Kolom Kiri */}
        <div>
          <div className="border-b pb-4 mb-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              {number}
              <span className="ml-2 text-sm bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full">
                {status}
              </span>
            </h1>
            <p className="text-sm text-gray-500">
              Dibuat <span className="text-red-500">{new Date(created_at).toLocaleString()}</span>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <h2 className="text-lg font-semibold">Dari</h2>
              <p className="text-gray-600">{distributor.name}</p>
              <p className="text-gray-500">{distributor.address}</p>
              <p className="text-gray-500">{distributor.phone}, {distributor.email}</p>
            </div>
            <div>
              <h2 className="text-lg font-semibold">Tujuan</h2>
              <p className="text-gray-600">{outlet.name}</p>
              <p className="text-gray-500">{outlet.address}</p>
              <p className="text-gray-500">{outlet.phone}, {outlet.email}</p>
            </div>
          </div>

          <h2 className="text-lg font-semibold mb-2">Produk</h2>
          <table className="w-full text-left border-collapse mb-6">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2">No</th>
                <th className="border px-4 py-2">Name</th>
                <th className="border px-4 py-2">Price</th>
                <th className="border px-4 py-2">Qty</th>
                <th className="border px-4 py-2">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="border px-4 py-2 text-center">{index + 1}</td>
                  <td className="border px-4 py-2">{item.obat.name}</td>
                  <td className="border px-4 py-2">{formatRupiah(item.price)}</td>
                  <td className="border px-4 py-2 text-center">{item.qty}</td>
                  <td className="border px-4 py-2">{formatRupiah(item.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mb-6">
            <h2 className="text-lg font-semibold">Deskripsi</h2>
            <p className="text-gray-600">{description}</p>
          </div>

         {/* === Rincian Total Dinamis === */}
<div className="mt-4 border-t pt-4">
  <p className="flex justify-between text-gray-700">
    <span>Sub Total (Untaxed)</span>
    <span>{formatRupiah(poData.sub_amount || 0)}</span>
  </p>

  <p className="flex justify-between text-gray-700">
    <span>Diskon</span>
    <span>-{formatRupiah(poData.discount || 0)}</span>
  </p>

  <p className="flex justify-between text-gray-700">
    <span>Pajak (Tax)</span>
    <span>{formatRupiah(poData.tax || 0)}</span>
  </p>

  <p className="flex justify-between font-bold text-lg mt-2 border-t pt-2">
    <span>Total</span>
    <span>{formatRupiah(poData.total_amount || 0)}</span>
  </p>
</div>

        </div>

        {/* Kolom Kanan */}
        <div>
          <div className="bg-gray-100 p-6 rounded-lg shadow-md">
            <h2 className="text-lg font-semibold mb-4">Previous Invoice</h2>
            {previousInvoices.length === 0 ? (
              <p className="text-gray-500 mb-4">Belum ada invoice sebelumnya.</p>
            ) : (
              <ul className="mb-4 list-disc pl-5 text-gray-700">
                {previousInvoices.map((inv, idx) => (
                  <li key={idx}>{inv.number} - <b>{formatRupiah(inv.amount)}</b></li>
                ))}
              </ul>
            )}

            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">No Invoice dari Distributor</label>
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                className="w-full p-2 border rounded-lg"
                placeholder="Masukkan No Invoice"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">Amount</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full p-2 border rounded-lg"
                placeholder="Masukkan jumlah (Rp)"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">Deskripsi</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full p-2 border rounded-lg"
                placeholder="Tambahkan deskripsi"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full p-2 border rounded-lg"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">Paid Date</label>
              <input
                type="date"
                value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
                className="w-full p-2 border rounded-lg"
              />
            </div>
        {/* ⬇️ Perhitungan estimasi dan sisa */}
        <p className="flex justify-between text-gray-700">
              Estimasi yang harus dibayar <span>{formatRupiah(estimatedAmount)}</span>
            </p>
            <p className="flex justify-between text-gray-700">
              Amount <span>{formatRupiah(Number(amount))}</span>
            </p>
            <p className="flex justify-between text-gray-700">
              Sisa Amount yang harus dibayar{" "}
              <span className={remainingAmount < 0 ? "text-red-500" : ""}>
                {formatRupiah(remainingAmount)}
              </span>
            </p>
            <div className="flex justify-end mt-6">
              <button
                className="bg-green-600 text-white px-6 py-2 rounded-lg flex items-center gap-2"
                onClick={handleSave}
              >
                <CheckIcon className="w-5 h-5" />
                Simpan
              </button>
            </div>
          </div>
        </div>
      </div>
 {/* Pop-up Modal */}
{showSuccessModal && (
  <SuccessModal
    message="Invoice berhasil disimpan!"
    onClose={() => {
      setShowSuccessModal(false);

      // Simpan current URL ke sessionStorage
      sessionStorage.setItem("previousPage", window.location.href);

      // Redirect ke halaman PO setelah menyimpan invoice
      window.location.href = `/enterprise/po`;
    }}
  />
)}

    </div>
  );
};

export default InvoiceFormPO;

